import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isEqual } from 'lodash';
import { PencilModalSizeEnum } from 'src/app/standalones/components/modals/modal-template/modal-template.component';
import { ScreenSizeService } from 'src/app/services/screen-size.service';
import { PencilDropdownButtonOption } from 'src/app/standalones/components/pencil-drop-down-button/pencil-drop-down-button.component';
import { TranslateService } from '@ngx-translate/core';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import { DomListenerFactoryService } from 'src/app/services/dom-listener-factory.service';
import { RealtimeSpaceService } from '../../../services/realtime-space.service';
import { Frame, FramePrivacy } from '../../../models/session';
import { SessionSharedDataService } from '../../../services/session-shared-data.service';
import { SpaceBoardsService } from '../../../services/space-boards.service';

@UntilDestroy()
@Component({
  selector: 'app-wb-users-frame-access-modal',
  templateUrl: './wb-users-frame-access-modal.component.html',
  styleUrls: ['./wb-users-frame-access-modal.component.scss'],
})
export class WbUsersFrameAccessModalComponent implements OnInit, OnDestroy {
  currentPrivacyForFrame = FramePrivacy.PUBLIC; // the selected privacy
  backupPrivacyForFrame = FramePrivacy.PUBLIC; // old selected privacy
  selectedPrivateUsersForFrame: string[] = [];
  backupPrivateUsersForFrame: string[] = []; // old private users in case of private
  sessionId = '';
  roomId = '';
  selectedFrame: Frame;
  FramePrivacy = FramePrivacy;

  showConfirmAddingNewFrameModal = false;
  canSwitchPrivacy = true;
  pencilModalSizeEnum = PencilModalSizeEnum;
  renderedAsMatBottomSheet = this.screenSizeService.renderMenuAsBottomSheet$;
  IconTypes = IconTypes;

  privacyOptions: PencilDropdownButtonOption[] = [
    {
      id: FramePrivacy.PRIVATE,
      value: this.translateService.instant('Private'),
    },
    {
      id: FramePrivacy.PUBLIC,
      value: this.translateService.instant('Everyone'),
    },
  ];
  selectedPrivacyOption?: PencilDropdownButtonOption = this.privacyOptions[0];
  domListener = this.domListenerFactoryService.createInstance();

  constructor(
    private router: Router,
    private realtimeSpaceService: RealtimeSpaceService,
    private sessionSharedDataService: SessionSharedDataService,
    private spaceBoardsService: SpaceBoardsService,
    @Optional() private dialogRef: MatDialogRef<WbUsersFrameAccessModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      sessionId: string;
      roomId: string;
      frame: Frame;
      canSwitchPrivacy?: boolean;
    },
    private screenSizeService: ScreenSizeService,
    private translateService: TranslateService,
    private domListenerFactoryService: DomListenerFactoryService,
  ) {
    this.sessionId = data.sessionId;
    this.roomId = data.roomId;
    this.selectedFrame = data.frame;
    this.canSwitchPrivacy = data.canSwitchPrivacy ?? true;
    // public is the default privacy
    this.currentPrivacyForFrame = this.selectedFrame.privacy || FramePrivacy.PUBLIC;
    this.backupPrivacyForFrame = this.currentPrivacyForFrame;

    // backup of users selected
    this.backupPrivateUsersForFrame = this.selectedFrame.privateUsers || [];
    this.selectedPrivateUsersForFrame = [...this.backupPrivateUsersForFrame];
    this.updateSelectedPrivacyOption();
  }

  ngOnInit(): void {
    this.setupEscKeyListener();
    this.router.events.pipe(untilDestroyed(this)).subscribe(() => this.dialogRef.close());
  }

  /**
   * set the new privacy for the frame ( public, private )
   * best on the selected new frame privacy from the radio buttons
   * @param value
   */
  handlePrivacyChange(value: FramePrivacy): void {
    this.currentPrivacyForFrame = value;
    if (this.currentPrivacyForFrame === FramePrivacy.PUBLIC) {
      // clear selected users
      this.selectedPrivateUsersForFrame = [];
    }
    // show the latest selected users before save
    if (this.currentPrivacyForFrame === FramePrivacy.PRIVATE) {
      this.selectedPrivateUsersForFrame = [...this.backupPrivateUsersForFrame];
    }
    this.updateSelectedPrivacyOption();
  }

  /**
   * close the modal
   */
  close(value?: any): void {
    this.dialogRef.close(value);
  }

  /**
   * check if we should disable the confirm button
   * if no data has changed
   */
  shouldConfirmButtonDisabled(): boolean {
    // if popup opened on private mode directly
    if (this.data.canSwitchPrivacy === false) {
      return false;
    }

    // in case we selected public
    // button should be disabled if the previous backup was public
    if (this.currentPrivacyForFrame === FramePrivacy.PUBLIC) {
      return this.currentPrivacyForFrame === this.backupPrivacyForFrame;
    }
    // in case of private
    if (this.currentPrivacyForFrame === FramePrivacy.PRIVATE) {
      // button should be enabled in case private was selected and there are users selected
      if (this.backupPrivacyForFrame === FramePrivacy.PUBLIC) {
        return false;
      }
      // if the backup was private, button should be enabled in case of new users selected
      if (this.backupPrivacyForFrame === FramePrivacy.PRIVATE) {
        return isEqual(this.selectedPrivateUsersForFrame, this.backupPrivateUsersForFrame);
      }
    }
    return true;
  }

  /**
   * used to save new privacy settings for the frame
   *  if it is private => then we have to make sure the selected users array isn't empty
   *  if it is public => save it and now everyone should have access to the frame
   */
  handleConfirm(): void {
    if (this.spaceBoardsService.isFrameLastPublicFrame(this.selectedFrame)) {
      // show the modal to ask for creating a new public frame
      this.showConfirmAddingNewFrameModal = true;

      return;
    }
    this.saveSelectedFramePrivacyAndClose();
  }

  /**
   * will run if the user accept to create a new public frame
   * as all frames will be private we need to create a new public frame
   */
  handleCreatePublicFrameButton(): void {
    this.sessionSharedDataService.createNewPublicFrame.next(true);
    this.saveSelectedFramePrivacyAndClose();
  }

  /**
   * will save the new frame privacy inside yjs and close the modal
   * @private
   */
  private saveSelectedFramePrivacyAndClose() {
    this.selectedFrame.privacy = this.currentPrivacyForFrame;
    if (this.selectedFrame.privacy === FramePrivacy.PUBLIC) {
      this.selectedFrame.privateUsers = [];
    }
    if (this.selectedFrame.privacy === FramePrivacy.PRIVATE) {
      this.selectedFrame.privateUsers = this.selectedPrivateUsersForFrame;
    }

    this.realtimeSpaceService.service.updateFramePrivacy(
      this.selectedFrame,
      this.sessionId,
      this.roomId,
    );
    this.close(true);
  }

  private updateSelectedPrivacyOption(): void {
    this.selectedPrivacyOption =
      this.currentPrivacyForFrame === FramePrivacy.PRIVATE
        ? this.privacyOptions[0]
        : this.privacyOptions[1];
  }

  handleParticipantsSelectionChange(selectedParticipants: string[]): void {
    this.selectedPrivateUsersForFrame = selectedParticipants;
  }

  private setupEscKeyListener() {
    this.domListener.add(
      'document',
      'keyup.esc',
      () => {
        this.dialogRef.close();
      },
      true,
    );
  }

  ngOnDestroy(): void {
    this.domListener.clear();
  }
}
